import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  createSupabaseServerClient,
  generateSlug,
  uploadImageToStorage,
  geocodeLocation,
  normalizeTags,
  withoutTestAds,
} from "./ads.server";
import { CATEGORY_SLUGS } from "./categories";
import type { AdminTraceStep } from "./admin-trace";
import { distanceKm } from "./geo-jitter";
import { smartTruncate } from "./smart-truncate";
import type { Lang } from "./i18n/config";

import { readPlanAndUsage } from "./plans.server";
import { checkQuota, limitsFor, PLANS } from "./plans";
import { issuesToMessage } from "./moderation";
import { enforceRateLimit, optionalUserId } from "./rate-limit.server";
import {
  AD_LIMITS,
  AUCTION_MIN_START_PRICE,
  adCoreSchema,
  auctionReserveFloor,
} from "./ad-rules";
import { AUCTION_LOCK_MESSAGE, auctionLockState } from "./auctions";
import {
  guardImageCount,
  guardPeople,
  guardPrice,
  guardRateAndQuota,
  guardText,
  guardVideo,
  resolveContact,
} from "./ad-guard.server";


const ImageInput = z.object({
  imageBase64: z.string().min(1),
  mimeType: z.string().min(1),
});

// Alla uppladdade bilder + ev. bildrutor ur videon skickas till AI:n.
const GenerateAdInput = z.object({
  images: z.array(ImageInput).min(1).max(7),
  // True när sista bilden är en bildruta ur säljarens video (äldre klienter).
  videoFrame: z.boolean().optional(),
  // Hur många av de SISTA bilderna som är bildrutor ur säljarens film.
  videoFrames: z.number().int().min(0).max(4).optional(),
  // Säljaren har valt ett annat föremål ur bilden – annonsen skrivs om på det.
  focusItem: z.string().trim().max(80).optional(),
});

const PublishAdInput = z.object({
  images: z.array(ImageInput).min(1).max(AD_LIMITS.imagesMax),
  videoPath: z.string().trim().max(200).optional().or(z.literal("")),
  ...adCoreSchema,
  // Interna AI-taggar – syns aldrig publikt, används bara för matchning.
  tags: z.array(z.string().max(40)).max(12).optional(),
  // Säljaren intygar att samtycke finns för personer som syns på bilderna.
  peopleConsent: z.boolean().optional(),
  // Resultatet från prisgranskningen (görs om på servern om det saknas).
  priceFlag: z.enum(["low", "high"]).nullable().optional(),
  priceNote: z.string().max(300).optional(),
  priceMin: z.number().int().nonnegative().nullable().optional(),
  priceMax: z.number().int().nonnegative().nullable().optional(),
  // Säljarens motivering när priset kräver vårt godkännande.
  priceApprovalMotivation: z.string().trim().max(500).optional(),

  // Idempotensnyckel per utkast – hindrar dubbletter om publiceringen görs om
  // efter avbrott (stängd flik, tappad uppkoppling, kraschad dator).
  publishKey: z.string().trim().max(80).optional(),
  // Fast pris eller auktion. Vid auktion är price utgångspriset.
  saleKind: z.enum(["fixed", "auction"]).optional(),
  reservePrice: z.number().int().positive().optional().nullable(),
  auctionDays: z.union([z.literal(3), z.literal(7), z.literal(14)]).optional(),
  /** Säljaren tar emot bud i meddelanden (fast pris). Default på. */
  acceptsOffers: z.boolean().optional(),
  /** Dold annons – bara superadmin kan sätta den och bara superadmin ser den. */
  hidden: z.boolean().optional(),
  contentLanguage: z.enum(["sv", "en", "es"]).optional(),
  /** Säljland – annonsen visas bara på den marknaden. */
  country: z.enum(["SE", "ES"]).optional(),
});

const UpdateAdInput = z.object({
  slug: z.string().min(1),
  ...adCoreSchema,
  // Bilder i vald ordning – befintliga anges med path, nya med base64.
  images: z
    .array(z.union([z.object({ path: z.string().min(1).max(300) }), ImageInput]))
    .min(1)
    .max(AD_LIMITS.imagesMax)
    .optional(),
  peopleConsent: z.boolean().optional(),
  // Auktionsvillkor – tas bara emot när auktionen saknar bud.
  reservePrice: z.number().int().positive().optional().nullable(),
  auctionDays: z.union([z.literal(3), z.literal(7), z.literal(14)]).optional(),
  // Video: sträng = ny/befintlig sökväg, "" = ta bort, undefined = lämna orörd.
  videoPath: z.string().trim().max(300).optional(),
  contentLanguage: z.enum(["sv", "en", "es"]).optional(),
  /** Säljland – annonsen visas bara på den marknaden. */
  country: z.enum(["SE", "ES"]).optional(),
});

const CheckPriceInput = z.object({
  title: z.string().trim().min(1).max(120),
  description: z.string().trim().max(4000),
  category: z.string().max(60).optional(),
  price: z.number().int().positive(),
});


/** AI-granskning av priset – används innan publicering/sparande för att varna säljaren. */
export const checkAdPrice = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => CheckPriceInput.parse(input))
  .handler(async ({ data, context }) => {
    const { assessPrice } = await import("./price-check.server");
    return assessPrice({ ...data, userId: context.userId });
  });

const GetAdInput = z.object({ slug: z.string().min(1) });

const ListAdsInput = z.object({
  category: z.string().optional(),
  categories: z.array(z.string().max(60)).max(20).optional(),
  search: z.string().max(80).optional(),
  limit: z.number().int().positive().max(1000).optional(),
  offset: z.number().int().min(0).max(100000).optional(),
  sort: z.enum(["senaste", "billigast", "dyrast", "nara", "slutar-snart"]).optional(),
  saleKind: z.enum(["fixed", "auction"]).optional(),
  /** Filtrera på säljartyp: privatperson eller företag. */
  sellerType: z.enum(["private", "business"]).optional(),
  /** Marknad – bara annonser i det valda säljlandet. */
  country: z.enum(["SE", "ES"]).optional(),
});

const ListAdsMapInput = z.object({
  category: z.string().optional(),
  categories: z.array(z.string().max(60)).max(20).optional(),
  search: z.string().max(80).optional(),
  saleKind: z.enum(["fixed", "auction"]).optional(),
  sellerType: z.enum(["private", "business"]).optional(),
  country: z.enum(["SE", "ES"]).optional(),
});

/** Alla konton som säljer som företag. Används både för filter och för märkningen. */
async function businessUserIds(supabase: ReturnType<typeof createSupabaseServerClient>) {
  const { data } = await supabase
    .from("profiles")
    .select("user_id")
    .eq("account_type", "business")
    .limit(5000);
  return (data ?? []).map((r) => r.user_id as string);
}

/** Lägger på säljartypsfiltret. Returnerar false när filtret inte kan ge några träffar. */
function applySellerTypeFilter<T>(query: T, sellerType: string | undefined, ids: string[]): T | null {
  if (!sellerType) return query;
  const q = query as any;
  if (sellerType === "business") return ids.length ? q.in("user_id", ids) : null;
  return ids.length ? q.not("user_id", "in", `(${ids.join(",")})`) : q;
}

// En grupp = ett föremål eller en samling föremål som hör naturligt ihop i samma annons.
// OBS: contact_email och contact_phone är aldrig publika – de exponeras inte via dessa läsningar.
const AD_COLUMNS =
  "id, slug, image_url, image_urls, image_focus, video_url, address, latitude, longitude, title, description, content_language, price, price_flag, price_flag_note, category, location, contact_name, created_at, user_id, is_paused, promoted_until, expires_at, archived_at, sold_at, sold_price, sale_kind, start_price, reserve_price, auction_ends_at, auction_status, accepts_offers, hidden, country, price_includes_vat";

async function requestLanguage(): Promise<Lang> {
  try {
    const { getRequest } = await import("@tanstack/react-start/server");
    const request = getRequest();
    const pathname = new URL(request.url).pathname;
    if (pathname === "/en" || pathname.startsWith("/en/")) return "en";
    if (pathname === "/es" || pathname.startsWith("/es/")) return "es";
    const cookie = request.headers.get("cookie") ?? "";
    const value = cookie.match(/(?:^|; )sellavo-lang=(sv|en|es)/)?.[1];
    return value === "en" || value === "es" ? value : "sv";
  } catch {
    return "sv";
  }
}

async function localizeAds<T extends { id?: string; title: string; description: string }>(
  supabase: ReturnType<typeof createSupabaseServerClient>,
  ads: T[],
): Promise<T[]> {
  const ids = ads.map((ad) => ad.id).filter((id): id is string => !!id);
  if (!ids.length) return ads;
  const { data } = await supabase
    .from("ad_translations")
    .select("ad_id, title, description")
    .eq("language", await requestLanguage())
    .in("ad_id", ids);
  const byId = new Map((data ?? []).map((row) => [row.ad_id, row]));
  return ads.map((ad) => {
    const localized = ad.id ? byId.get(ad.id) : null;
    return localized ? { ...ad, title: localized.title, description: localized.description } : ad;
  });
}

export const generateAd = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => GenerateAdInput.parse(input))
  .handler(async ({ data }) => {
    // Publik funktion (man ska kunna testa utan konto) – därför spärr per konto/IP.
    const userId = await optionalUserId();
    await enforceRateLimit("ai_generate", userId);
    const { withAdminTrace } = await import("./admin-trace.server");
    const { traceSpan, traceNote } = await import("./scan-trace.server");

    const { result: final, trace } = await withAdminTrace(userId, async () => {
      const { runAdWriter } = await import("./ad-writer.server");
      const result = await traceSpan(
        {
          name: "Bildanalys – vad är det för pryl?",
          service: "Lovable AI",
          why: "Läser alla bilder, grupperar föremål och skriver rubrik, text, kategori och prisförslag.",
          request: { bilder: data.images.length, videorutor: data.videoFrames ?? 0 },
        },
        () =>
          runAdWriter(
            data.images,
            data.videoFrames ?? (data.videoFrame ? 1 : 0),
            userId,
            data.focusItem || null,
          ),
        (r) => ({
          rubrik: r.title,
          pris: r.price,
          kategori: r.category,
          sammanhang: (r as { cohesion?: string }).cohesion ?? "",
          grupper: (r as { groups?: unknown[] }).groups?.length ?? 0,
        }),
      );
      const output = result as typeof result & {
        highValue?: boolean;
        priceUncertain?: boolean;
        priceLow?: number;
        priceHigh?: number;
      };

      // Svårbedömda prylar (konst, antikt, samlarobjekt) får en tyst extrakoll med en
      // tyngre modell innan vi sätter rubrik och pris. Utloggade får några per dygn.
      if (!output.highValue && !output.priceUncertain) {
        traceNote({
          name: "Beslut: ingen extrakoll",
          why: "Prylen bedömdes varken som högt värde eller osäkert pris.",
          response: { highValue: false, priceUncertain: false },
        });
        return output;
      }
      const { tryRateLimit } = await import("./rate-limit.server");
      if (!(await tryRateLimit("quick_id", userId))) {
        traceNote({
          name: "Beslut: extrakoll hoppades över",
          why: "Kvoten för djupare identifiering var slut för perioden.",
          ok: false,
        });
        return output;
      }
      try {
        const { quickIdentify, refineAdWithIdentification } = await import("./quick-id.server");
        const id = await traceSpan(
          {
            name: "Extrakoll – identifiera föremålet",
            service: "Lovable AI",
            why:
              output.highValue
                ? "Bedömdes som högt värde – tyngre modell får avgöra vad det är."
                : "Priset var osäkert – tyngre modell får avgöra vad det är.",
          },
          () => quickIdentify(data.images, userId),
          (r) => ({ objekt: r?.object ?? "", prisLåg: r?.priceLow ?? null, prisHög: r?.priceHigh ?? null }),
        );
        if (!id?.object) return output;
        const refined = await traceSpan(
          {
            name: "Skriv om annonsen med identifieringen",
            service: "Lovable AI",
            why: "Rubrik, text, pris och kategori justeras efter vad prylen visade sig vara.",
            request: { objekt: id.object },
          },
          () =>
            refineAdWithIdentification({
              id,
              title: output.title,
              description: output.description,
              price: output.price,
              category: output.category,
              userId,
            }),
          (r) => ({ rubrik: r?.title ?? "", pris: r?.price ?? null, kategori: r?.category ?? "" }),
        );
        if (!refined) return { ...output, identifiedAs: id.object, preliminary: true };
        return {
          ...output,
          title: refined.title || output.title,
          description: refined.description || output.description,
          price: refined.price || output.price,
          category:
            refined.category && CATEGORY_SLUGS.includes(refined.category)
              ? refined.category
              : output.category,
          priceLow: id.priceLow ?? output.priceLow,
          priceHigh: id.priceHigh ?? output.priceHigh,
          // Vad extrakollen kom fram till – används som motivering i djupvärderingen
          // och som "preliminär text" i annonsen om säljaren hoppar över den.
          identifiedAs: id.object,
          preliminary: true,
        };

      } catch (error) {
        console.error("[generateAd] extrakollen misslyckades", error);
        return output;
      }
    });

    return { ...final, adminTrace: trace };
  });


/** Hur många AI-omskrivningar användaren har kvar den här månaden. */
export const getMyRewriteQuota = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { plan, unlimited } = await readPlanAndUsage(context.supabase as never, context.userId);
    const limit = limitsFor(plan, unlimited).aiRewritesPerMonth;
    const since = new Date();
    since.setDate(since.getDate() - 30);
    const { count } = await context.supabase
      .from("ad_rewrites")
      .select("id", { count: "exact", head: true })
      .eq("user_id", context.userId)
      .gte("created_at", since.toISOString());
    const used = count ?? 0;
    return {
      plan,
      limit,
      used,
      remaining: limit === null ? null : Math.max(0, limit - used),
    };
  });

/**
 * Skriver om en befintlig annons med AI utifrån annonsens egna bilder.
 * Räknas mot planens månadskvot för omskrivningar.
 */
export const rewriteAdText = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1) }).parse(input))
  .handler(async ({ data, context }) => {
    await enforceRateLimit("ai_generate", context.userId);

    const { plan, unlimited } = await readPlanAndUsage(context.supabase as never, context.userId);
    const limit = limitsFor(plan, unlimited).aiRewritesPerMonth;
    const since = new Date();
    since.setDate(since.getDate() - 30);
    const { count } = await context.supabase
      .from("ad_rewrites")
      .select("id", { count: "exact", head: true })
      .eq("user_id", context.userId)
      .gte("created_at", since.toISOString());
    const used = count ?? 0;
    if (limit !== null && used >= limit) {
      throw new Error(
        plan === "free"
          ? `Du har använt dina ${limit} AI-omskrivningar den här månaden. Uppgradera till Plus för 40 per månad.`
          : `Du har använt dina ${limit} AI-omskrivningar den här månaden. Uppgradera till Pro för obegränsat.`,
      );
    }

    const { data: ad, error } = await context.supabase
      .from("ads")
      .select("id, image_url, image_urls")
      .eq("slug", data.slug)
      .eq("user_id", context.userId)
      .maybeSingle();
    if (error || !ad) throw new Error("Annonsen hittades inte.");

    const paths = ((ad.image_urls?.length ? ad.image_urls : [ad.image_url]) as string[])
      .filter(Boolean)
      .slice(0, 6);
    if (!paths.length) throw new Error("Annonsen saknar bilder att analysera.");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const images: { imageBase64: string; mimeType: string }[] = [];
    for (const path of paths) {
      const { data: blob, error: dlError } = await supabaseAdmin.storage
        .from("ad-images")
        .download(path);
      if (dlError || !blob) continue;
      const buffer = Buffer.from(await blob.arrayBuffer());
      images.push({
        imageBase64: buffer.toString("base64"),
        mimeType: blob.type || "image/jpeg",
      });
    }
    if (!images.length) throw new Error("Kunde inte läsa annonsens bilder.");

    const { runAdWriter } = await import("./ad-writer.server");
    const { runWithAiContext } = await import("./ai-usage.server");
    const output = await runWithAiContext(
      { operation: "ad_rewrite", userId: context.userId, adId: ad.id },
      () => runAdWriter(images, 0, context.userId),
    );


    await context.supabase
      .from("ad_rewrites")
      .insert({ user_id: context.userId, ad_id: ad.id });

    return {
      title: output.title,
      description: output.description,
      price: output.price,
      category: output.category ?? null,
      tags: output.tags ?? [],
      imageNotes: output.imageNotes ?? [],
      imageCount: images.length,
      remaining: limit === null ? null : Math.max(0, limit - used - 1),
    };
  });


export const publishAd = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => PublishAdInput.parse(input))
  .handler(async ({ data, context }) => {
    // Admin får en logg över alla steg och beslut. Vanliga användare spårar vi inte.
    const { isAdminUserId, toAdminTrace } = await import("./admin-trace.server");
    const { startTrace, traceSpan, traceNote } = await import("./scan-trace.server");
    const tracer = (await isAdminUserId(context.userId)) ? startTrace() : null;
    const withTrace = (): { adminTrace: AdminTraceStep[] | null } => ({
      adminTrace: tracer ? toAdminTrace(tracer.finish()) : null,
    });

    // Har samma publicering redan gått igenom? Returnera den befintliga annonsen.
    if (data.publishKey) {
      const { data: already } = await context.supabase
        .from("ads")
        .select("slug")
        .eq("user_id", context.userId)
        .eq("publish_key", data.publishKey)
        .maybeSingle();
      if (already?.slug) {
        return { needsPeopleConsent: false as const, needsPriceApproval: false as const, slug: already.slug, url: `/a/${already.slug}`, ...withTrace() };
      }
    }

    // Gemensam kontrollkedja – samma som vid redigering.
    const guardCtx = { supabase: context.supabase as never, userId: context.userId, claims: context.claims as never };
    const { limits } = await traceSpan(
      {
        name: "Kontroll: hastighet och kvot",
        service: "Sellavo",
        why: "Stoppar spam och håller planens annonskvot.",
      },
      () => guardRateAndQuota("publish", guardCtx),
      (r) => ({ plan: (r as { plan?: string }).plan ?? "", bilder: (r as any).limits?.imagesPerAd ?? null }),
    );
    guardImageCount(data.images.length, limits);
    await traceSpan(
      {
        name: "Moderering av rubrik och text",
        service: "Lovable AI",
        why: "Fångar förbjudet innehåll och texter som blandar helt olika varor.",
        request: { rubrik: data.title },
      },
      () => guardText(data.title, data.description, context.userId),
    );
    await guardVideo(data.videoPath, guardCtx, limits);

    const peopleFound = await traceSpan(
      {
        name: "Personer i bild",
        service: "Lovable AI",
        why: "Bilder med igenkännbara personer kräver samtycke innan publicering.",
      },
      () => guardPeople(data.images, data.peopleConsent, context.userId),
      (r) => ({ kräverSamtycke: !!r }),
    );
    if (peopleFound) {
      // Returneras som resultat (inte kast) så klienten kan be om samtycke utan runtime-fel.
      return { needsPeopleConsent: true as const, needsPriceApproval: false as const, slug: "", url: "", ...withTrace() };
    }


    const contact = await resolveContact(guardCtx, {
      contactName: data.contactName,
      contactEmail: data.contactEmail,
      contactPhone: data.contactPhone,
    });

    const imagePaths: string[] = [];
    for (const img of data.images) {
      imagePaths.push(await uploadImageToStorage(img.imageBase64, img.mimeType));
    }

    // Var i varje bild finns själva prylen? Rutan används för att visa bilden
    // inzoomad på rätt ställe i kort och galleri. Misslyckas den visas hela
    // bilden som förut – publiceringen får aldrig falla på det här.
    const imageFocus = await traceSpan(
      {
        name: "Hitta prylen i bilderna",
        service: "Lovable AI",
        why: "Markerar var föremålet finns så att bilderna kan visas inzoomade på rätt ställe.",
        request: { bilder: data.images.length },
      },
      async () => {
        const { detectImageFocus } = await import("./image-focus.server");
        return detectImageFocus(data.images, context.userId);
      },
      (r) => ({ hittade: r.filter(Boolean).length }),
    );


    const geo = await geocodeLocation(data.location, data.address);

    const { data: languageProfile } = await context.supabase
      .from("profiles")
      .select("language")
      .eq("user_id", context.userId)
      .maybeSingle();
    const profileLanguage = languageProfile?.language;
    const sourceLanguage: Lang = data.contentLanguage ??
      (profileLanguage === "en" || profileLanguage === "es" ? profileLanguage : "sv");
    const { translateAd, adSourceHash } = await import("./ad-translation.server");
    // Översättning får aldrig blockera publicering – misslyckas den sparas
    // annonsen på sitt eget språk och kan översättas vid nästa sparning.
    const translations = await traceSpan(
      {
        name: "Översätter annonsen",
        service: "Lovable AI",
        why: "Skapar svenska, engelska och spanska versioner för köpare.",
      },
      () =>
        translateAd({ title: data.title, description: data.description, sourceLanguage, userId: context.userId }).catch(
          (err) => {
            console.error("[publishAd] översättning misslyckades", err);
            return [
              {
                language: sourceLanguage,
                title: data.title,
                description: data.description,
                sourceHash: adSourceHash(data.title, data.description),
              },
            ];
          },
        ),
      () => ({ språk: ["sv", "en", "es"] }),
    );

    let slug = generateSlug();
    let attempts = 0;
    while (attempts < 5) {
      const { data: existing } = await context.supabase
        .from("ads")
        .select("slug")
        .eq("slug", slug)
        .maybeSingle();
      if (!existing) break;
      slug = generateSlug();
      attempts++;
    }

    // Taggar är grunden för "liknande annonser" – saknas de så tar AI:n fram dem nu,
    // med befintliga taggar som ordlista så att matchningen hänger ihop.
    const { ensureTags } = await import("./tags.server");
    const tags = await traceSpan(
      {
        name: "Taggar för liknande annonser",
        service: "Lovable AI",
        why: "Taggarna styr matchningen mot andra annonser på sajten.",
        request: { kategori: data.category, befintliga: data.tags ?? [] },
      },
      () =>
        ensureTags({
          title: data.title,
          description: data.description,
          category: data.category,
          current: data.tags,
          userId: context.userId,
        }),
      (r) => ({ taggar: r }),
    );

    // Prisgranskning + spärr för orimligt pris – samma steg som vid redigering.
    const priced = await traceSpan(
      {
        name: "Prisgranskning",
        service: "Lovable AI",
        why: "Jämför säljarens pris mot ett rimligt spann och stoppar orimliga priser.",
        request: { pris: data.price },
      },
      () =>
        guardPrice({
          title: data.title,
          description: data.description,
          category: data.category,
          price: data.price,
          userId: context.userId,
          provided: {
            flag: data.priceFlag,
            note: data.priceNote,
            min: data.priceMin,
            max: data.priceMax,
          },
        }),
      (r) => ({ flagga: r.flag, notering: r.note, spärrad: r.gate.blocked, min: r.gate.min, max: r.gate.max }),
    );
    const priceFlag = priced.flag;
    const priceNote = priced.note;
    const priceGate = priced.gate;
    if (priceGate.blocked && !data.priceApprovalMotivation) {
      traceNote({
        name: "Beslut: publicering pausad för prisgodkännande",
        why: "Priset ligger utanför AI:ns spann och kräver en motivering.",
        ok: false,
      });
      return {
        needsPeopleConsent: false as const,
        needsPriceApproval: true as const,
        slug: "",
        url: "",
        priceMin: priceGate.min,
        priceMax: priceGate.max,
        ...withTrace(),
      };
    }




    // Auktion: priset i annonsen är utgångspriset, och slutdatumet sätts nu.
    const saleKind = data.saleKind ?? "fixed";
    if (saleKind === "auction") {
      const { readSettings } = await import("./orders.server");
      if (!(await readSettings()).auctionsEnabled) {
        throw new Error("Auktioner är inte igång just nu. Lägg ut prylen med fast pris i stället.");
      }
      if (data.price < AUCTION_MIN_START_PRICE) {
        throw new Error(
          `Utgångspriset på en auktion måste vara minst ${AUCTION_MIN_START_PRICE} kr.`,
        );
      }
    }
    const auctionFields =
      saleKind === "auction"
        ? {
            sale_kind: "auction",
            auction_status: "live",
            start_price: data.price,
            // Skyddsnät: reservationspriset kan aldrig hamna under utgångspriset
            // eller under det absoluta minimibeloppet.
            reserve_price: Math.max(
              data.reservePrice ?? 0,
              auctionReserveFloor(data.price),
            ),
            auction_ends_at: new Date(
              Date.now() + (data.auctionDays ?? 7) * 24 * 60 * 60 * 1000,
            ).toISOString(),
          }
        : {};

    const { data: inserted, error } = await context.supabase
      .from("ads")
      .insert({
        slug,
        image_url: imagePaths[0],
        image_urls: imagePaths,
        image_focus: imageFocus,
        video_url: data.videoPath || null,
        address: data.address || "",
        latitude: geo?.latitude ?? null,
        longitude: geo?.longitude ?? null,
        title: data.title,
        description: data.description,
        content_language: sourceLanguage,
        price: data.price,
        price_flag: priceFlag,
        price_flag_note: priceFlag ? priceNote || null : null,
        category: data.category,
        location: data.location,
        country: data.country ?? "SE",
        contact_name: contact.contactName,
        contact_email: contact.contactEmail,
        contact_phone: contact.contactPhone || null,

        tags,
        accepts_offers: data.acceptsOffers !== false,
        hidden: data.hidden === true && (await (await import("@/lib/admin-guard.server")).isSuperadmin(context as never)),
        publish_key: data.publishKey || null,
        user_id: context.userId,
        ...(priceGate.blocked
          ? {
              is_paused: true,
              moderation_state: "awaiting_price_approval",
              moderation_reason: "Väntar på godkännande av högt pris.",
            }
          : {}),
        ...auctionFields,
      } as never)
      .select("id")
      .maybeSingle();

    if (error) throw new Error(`Kunde inte publicera annonsen: ${error.message}`);

    const newAdId = (inserted as any)?.id as string | undefined;
    if (!newAdId) throw new Error("Annonsen skapades utan ett giltigt id.");
    const { error: translationError } = await context.supabase.from("ad_translations").upsert(
      translations.map((item) => ({
        ad_id: newAdId,
        language: item.language,
        title: item.title,
        description: item.description,
        source_hash: item.sourceHash,
      })),
      { onConflict: "ad_id,language" },
    );
    if (translationError) {
      // Annonsen är publicerad – språkversionerna får saknas tills nästa sparning.
      console.error("[publishAd] kunde inte spara översättningar", translationError);
    }

    if (priceGate.blocked && (inserted as any)?.id) {
      await context.supabase.from("ad_price_requests").insert({
        ad_id: (inserted as any).id,
        user_id: context.userId,
        requested_price: data.price,
        ai_min: priceGate.min,
        ai_max: priceGate.max,
        motivation: (data.priceApprovalMotivation ?? "").slice(0, 500),
      } as never);
    }


    // Koppla de AI-anrop som gjordes medan annonsen skapades till annonsen,
    // så att admininfon visar den verkliga kostnaden i stället för en schablon.
    if (newAdId) await linkRecentAiCallsToAd(newAdId, context.userId);

    // Kvitto till säljaren – får aldrig stoppa publiceringen.
    if (newAdId) {
      try {
        const { notifyAdPublished } = await import("@/lib/notifications.server");
        await notifyAdPublished({
          userId: context.userId,
          adId: newAdId,
          slug,
          title: data.title,
          price: data.price,
          category: data.category,
          location: data.location,
          saleKind,
          auctionEndsAt: (auctionFields as { auction_ends_at?: string }).auction_ends_at ?? null,
        });
      } catch (mailError) {
        console.error("[ads] kunde inte skicka publiceringsmejl", mailError);
      }
    }



    // Första annonsen: visningsnamnet från formuläret blir kontots namn.
    // Har profilen redan ett namn rör vi det inte.
    if (contact.shouldSaveName) {
      await context.supabase
        .from("profiles")
        .upsert(
          { user_id: context.userId, display_name: contact.contactName },
          { onConflict: "user_id" },
        );
    }


    return { needsPeopleConsent: false as const, needsPriceApproval: false as const, slug, url: `/a/${slug}`, ...withTrace() };
  });

/** AI-operationer som hör till en enskild annons (inte skanningar eller sökningar). */
const AD_AI_OPERATIONS = [
  "ad",
  "ad_rewrite",
  "rewrite",
  "tags",
  "quality",
  "price",
  "moderation_text",
  "moderation_people",
  "moderation_audio",
  "clean_image",
  "clean_image_cache",
];

/**
 * Kopplar användarens senaste okopplade AI-anrop till annonsen, så att
 * admininfon kan visa den verkliga kostnaden i stället för en schablon.
 */
async function linkRecentAiCallsToAd(adId: string, userId: string) {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin
      .from("ai_calls")
      .update({ ad_id: adId } as never)
      .is("ad_id", null)
      .eq("user_id", userId)
      .gte("created_at", new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString())
      .in("operation", AD_AI_OPERATIONS);
  } catch (error) {
    console.error("[ads] kunde inte koppla AI-anrop till annonsen", error);
  }
}

/**
 * Auktionens låsläge för säljarens egen annons. Används både av
 * redigeringen och av pausa/arkivera/radera/markera såld.
 */
async function loadAuctionLock(slug: string, userId: string) {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: ad } = await supabaseAdmin
    .from("ads")
    .select("id, sale_kind, auction_status, auction_ends_at, start_price, reserve_price")
    .eq("slug", slug)
    .eq("user_id", userId)
    .is("deleted_at", null)
    .maybeSingle();
  if (!ad) return { ad: null, lock: auctionLockState({}) };

  let bidCount = 0;
  let topBid: number | null = null;
  if ((ad as any).sale_kind === "auction") {
    const { count } = await supabaseAdmin
      .from("bids")
      .select("id", { count: "exact", head: true })
      .eq("ad_id", (ad as any).id);
    bidCount = count ?? 0;
    if (bidCount > 0) {
      const { data: top } = await supabaseAdmin
        .from("bids")
        .select("amount")
        .eq("ad_id", (ad as any).id)
        .order("amount", { ascending: false })
        .limit(1)
        .maybeSingle();
      topBid = (top as any)?.amount ?? null;
    }
  }

  return {
    ad: ad as any,
    lock: auctionLockState({
      saleKind: (ad as any).sale_kind,
      auctionStatus: (ad as any).auction_status,
      auctionEndsAt: (ad as any).auction_ends_at,
      bidCount,
      topBid,
      startPrice: (ad as any).start_price,
      reservePrice: (ad as any).reserve_price,
    }),
  };
}

/** Kastar fel om auktionen är låst (live med bud). */
async function assertAuctionUnlocked(slug: string, userId: string, action: string) {
  const { lock } = await loadAuctionLock(slug, userId);
  if (lock.locked) {
    throw new Error(
      `Auktionen pågår och har ${lock.bidCount} bud – du kan inte ${action}. ${AUCTION_LOCK_MESSAGE}`,
    );
  }
  return lock;
}



export const updateAd = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => UpdateAdInput.parse(input))
  .handler(async ({ data, context }) => {
    // Samma kontrollkedja som vid publicering – bara kvoten hoppas över,
    // eftersom annonsen redan finns.
    const guardCtx = { supabase: context.supabase as never, userId: context.userId, claims: context.claims as never };
    const { limits } = await guardRateAndQuota("update", guardCtx);
    await guardText(data.title, data.description, context.userId);

    // Annonsens nuvarande läge – behövs för taggar och för att veta om videon
    // är oförändrad (då är den redan granskad).
    const { data: existingAd } = await context.supabase
      .from("ads")
      .select("id, tags, video_url, content_language")
      .eq("slug", data.slug)
      .eq("user_id", context.userId)
      .maybeSingle();

    // Alla AI-anrop under redigeringen kopplas direkt till annonsen.
    const { setAiContext } = await import("./ai-usage.server");
    setAiContext({
      operation: "ad_update",
      userId: context.userId,
      adId: ((existingAd as any)?.id as string | undefined) ?? null,
    });

    await guardVideo(data.videoPath, guardCtx, limits, {
      alreadyOnAd: (existingAd?.video_url as string | null) ?? null,
    });

    // Auktioner: villkoren låses så fort det finns bud.
    const { lock: auctionLock } = await loadAuctionLock(data.slug, context.userId);
    if (auctionLock.locked && data.price !== (auctionLock.startPrice ?? data.price)) {
      throw new Error(
        `Utgångspriset kan inte ändras när auktionen har bud. ${AUCTION_LOCK_MESSAGE}`,
      );
    }



    // Bilder: ordningen i listan gäller, första bilden blir huvudbild.
    let imagePaths: string[] | null = null;
    if (data.images && data.images.length) {
      guardImageCount(data.images.length, limits);

      const newImages = data.images.filter(
        (img): img is { imageBase64: string; mimeType: string } => "imageBase64" in img,
      );
      if (await guardPeople(newImages, data.peopleConsent, context.userId)) {
        return {
          needsPeopleConsent: true as const,
          priceApprovalPending: false,
          priceMin: null,
          priceMax: null,
          slug: "",
        };
      }

      imagePaths = [];
      for (const img of data.images) {
        if ("path" in img) imagePaths.push(img.path);
        else imagePaths.push(await uploadImageToStorage(img.imageBase64, img.mimeType));
      }
    }

    const contact = await resolveContact(guardCtx, {
      contactName: data.contactName,
      contactEmail: data.contactEmail,
      contactPhone: data.contactPhone,
    });

    const geo = await geocodeLocation(data.location, data.address);

    const storedLanguage = existingAd?.content_language;
    const sourceLanguage: Lang = data.contentLanguage ??
      (storedLanguage === "en" || storedLanguage === "es" ? storedLanguage : "sv");
    const { translateAd, adSourceHash } = await import("./ad-translation.server");
    // Misslyckad översättning får inte hindra att ändringarna sparas.
    const translations = await translateAd({
      title: data.title,
      description: data.description,
      sourceLanguage,
      userId: context.userId,
      adId: (existingAd as { id?: string } | null)?.id ?? null,
    }).catch((err) => {
      console.error("[updateAd] översättning misslyckades", err);
      return [
        {
          language: sourceLanguage,
          title: data.title,
          description: data.description,
          sourceHash: adSourceHash(data.title, data.description),
        },
      ];
    });

    // Nytt pris eller ny text = ny prisbedömning, och samma spärr för
    // orimligt pris som vid publicering.
    const priced = await guardPrice({
      title: data.title,
      description: data.description,
      category: data.category,
      price: data.price,
      userId: context.userId,
    });
    const priceGate = priced.gate;

    // Saknar annonsen taggar (t.ex. publicerad innan taggningen fanns, eller
    // om AI:n felade då) så fyller vi på dem nu så att matchningen funkar.
    let tagPatch: { tags: string[] } | Record<string, never> = {};
    if (!((existingAd?.tags as string[] | null) ?? []).length) {
      const { ensureTags } = await import("./tags.server");
      const tags = await ensureTags({
        title: data.title,
        description: data.description,
        category: data.category,
        userId: context.userId,
      });
      if (tags.length) tagPatch = { tags };
    }

    // Auktionsvillkor: låsta när det finns bud, annars får säljaren justera
    // utgångspris, reservationspris och sluttid.
    let auctionPatch: Record<string, unknown> = {};
    if (auctionLock.isAuction && auctionLock.isLive) {
      if (auctionLock.locked) {
        auctionPatch = {};
      } else {
        if (data.price < AUCTION_MIN_START_PRICE) {
          throw new Error(
            `Utgångspriset på en auktion måste vara minst ${AUCTION_MIN_START_PRICE} kr.`,
          );
        }
        auctionPatch = {
          start_price: data.price,
          reserve_price: Math.max(
            data.reservePrice ?? auctionLock.reservePrice ?? 0,
            auctionReserveFloor(data.price),
          ),
          ...(data.auctionDays
            ? {
                auction_ends_at: new Date(
                  Date.now() + data.auctionDays * 24 * 60 * 60 * 1000,
                ).toISOString(),
              }
            : {}),
        };
      }
    }

    // Bilderna byttes ut – låt AI:n peka ut prylen i de nya bilderna igen.
    const focusPatch = imagePaths
      ? await (async () => {
          try {
            const { detectFocusForPaths } = await import("./image-focus.server");
            return await detectFocusForPaths(imagePaths, context.userId);
          } catch {
            return imagePaths.map(() => null);
          }
        })()
      : null;

    const { data: updated, error } = await context.supabase
      .from("ads")
      .update({
        title: data.title,
        description: data.description,
        content_language: sourceLanguage,
        // Priset på en låst auktion får aldrig skrivas om.
        ...(auctionLock.locked ? {} : { price: data.price }),
        ...auctionPatch,

        price_flag: priced.flag,
        price_flag_note: priced.flag ? priced.note || null : null,
        category: data.category,
        location: data.location,
        ...(data.country ? { country: data.country } : {}),
        address: data.address || "",
        latitude: geo?.latitude ?? null,
        longitude: geo?.longitude ?? null,
        contact_name: contact.contactName,
        contact_email: contact.contactEmail,
        contact_phone: contact.contactPhone || null,

        ...(imagePaths
          ? { image_url: imagePaths[0], image_urls: imagePaths, image_focus: focusPatch }
          : {}),
        ...(data.videoPath === undefined ? {} : { video_url: data.videoPath || null }),
        ...tagPatch,
        ...(priceGate.blocked
          ? {
              is_paused: true,
              moderation_state: "awaiting_price_approval",
              moderation_reason: "Väntar på godkännande av högt pris.",
            }
          : { moderation_state: "ok", moderation_reason: "" }),
      } as never)
      .eq("slug", data.slug)
      .eq("user_id", context.userId)
      .select("id, slug")
      .maybeSingle();

    if (error) throw new Error(`Kunde inte uppdatera annonsen: ${error.message}`);
    if (!updated) throw new Error("Annonsen hittades inte eller tillhör inte dig.");

    const { error: translationError } = await context.supabase.from("ad_translations").upsert(
      translations.map((item) => ({
        ad_id: updated.id,
        language: item.language,
        title: item.title,
        description: item.description,
        source_hash: item.sourceHash,
      })),
      { onConflict: "ad_id,language" },
    );
    if (translationError) console.error("[updateAd] kunde inte spara översättningar", translationError);

    // Även redigeringar kostar AI – koppla de anropen till annonsen.
    await linkRecentAiCallsToAd((updated as any).id, context.userId);

    if (priceGate.blocked) {
      await context.supabase.from("ad_price_requests").insert({
        ad_id: (updated as any).id,
        user_id: context.userId,
        requested_price: data.price,
        ai_min: priceGate.min,
        ai_max: priceGate.max,
        motivation: "",
      } as never);
    }

    return {
      needsPeopleConsent: false as const,
      priceApprovalPending: priceGate.blocked,
      priceMin: priceGate.min,
      priceMax: priceGate.max,
      slug: updated.slug,
    };
  });

/**
 * Tar bort annonsen (soft delete). Raden ligger kvar i databasen med deleted_at
 * så att vi kan hitta den i efterhand, men den syns varken publikt eller för ägaren.
 */
export const deleteAd = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => GetAdInput.parse(input))
  .handler(async ({ data, context }) => {
    await enforceRateLimit("ad_delete", context.userId);
    await assertAuctionUnlocked(data.slug, context.userId, "ta bort annonsen");

    const now = new Date().toISOString();
    const { data: updated, error } = await context.supabase
      .from("ads")
      .update({ deleted_at: now, archived_at: now, is_paused: true })
      .eq("slug", data.slug)
      .eq("user_id", context.userId)
      .is("deleted_at", null)
      .select("id, slug")
      .maybeSingle();
    if (error) throw new Error(`Kunde inte ta bort annonsen: ${error.message}`);
    if (!updated) throw new Error("Annonsen hittades inte eller tillhör inte dig.");

    const { refundActivePromotionsForAd } = await import("./promotions.server");
    await refundActivePromotionsForAd(context.supabase, context.userId, (updated as any).id);
    return { ok: true };
  });

/** Lägger annonsen i arkivet (dold publikt) eller plockar tillbaka den. */
export const setAdArchived = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        slug: z.string().min(1),
        archived: z.boolean(),
        // Sätts bara när säljaren uttryckligen svarat att prylen inte såldes.
        clearSold: z.boolean().optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    if (data.archived) await assertAuctionUnlocked(data.slug, context.userId, "arkivera annonsen");

    // Avarkivering får inte radera en genomförd försäljning. Sålda annonser
    // behåller försäljnings- och auktionshistoriken och förblir pausade.
    const { data: before } = await context.supabase
      .from("ads")
      .select("sold_at, expires_at")
      .eq("slug", data.slug)
      .eq("user_id", context.userId)
      .maybeSingle();
    const expired =
      !(before as any)?.expires_at || new Date((before as any).expires_at).getTime() < Date.now();
    const wasSold = Boolean((before as any)?.sold_at);
    const clearSold = !data.archived && wasSold && data.clearSold === true;

    // En såld annons får bara plockas tillbaka om säljaren svarat att den
    // inte såldes – annars ska försäljningen stå kvar i historiken.
    if (!data.archived && wasSold && !clearSold) {
      throw new Error(
        "Du kan inte ta tillbaka prylar som är sålda – lägg upp en ny annons i stället.",
      );
    }

    const renewIso = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString();
    const patch = data.archived
      ? { archived_at: new Date().toISOString(), is_paused: true }
      : clearSold
        ? {
            archived_at: null,
            is_paused: false,
            sold_at: null,
            sold_price: null,
            auction_status: "none",
            ...(expired ? { expires_at: renewIso } : {}),
          }
        : {
            archived_at: null,
            is_paused: false,
            ...(expired ? { expires_at: renewIso } : {}),
          };


    const { data: updated, error } = await context.supabase
      .from("ads")
      .update(patch)
      .eq("slug", data.slug)
      .eq("user_id", context.userId)
      .is("deleted_at", null)
      .select("id, slug, archived_at")
      .maybeSingle();
    if (error) throw new Error(`Kunde inte uppdatera annonsen: ${error.message}`);
    if (!updated) throw new Error("Annonsen hittades inte eller tillhör inte dig.");

    let refunded = 0;
    if (data.archived) {
      const { refundActivePromotionsForAd } = await import("./promotions.server");
      refunded = await refundActivePromotionsForAd(
        context.supabase,
        context.userId,
        (updated as any).id,
      );
    }
    return { slug: updated.slug, archived: !!updated.archived_at, refundedPromotions: refunded };
  });

/** Markerar en annons som såld (hamnar i arkivet och räknas in i försäljningsstatistiken). */
export const markAdSold = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        slug: z.string().min(1),
        soldPrice: z.number().int().min(0).max(100000000).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await assertAuctionUnlocked(data.slug, context.userId, "markera annonsen som såld");

    const { data: ad } = await context.supabase
      .from("ads")
      .select("id, price")
      .eq("slug", data.slug)
      .eq("user_id", context.userId)
      .is("deleted_at", null)
      .maybeSingle();
    if (!ad) throw new Error("Annonsen hittades inte eller tillhör inte dig.");

    const now = new Date().toISOString();
    const { error } = await context.supabase
      .from("ads")
      .update({
        sold_at: now,
        sold_price: data.soldPrice ?? (ad as any).price,
        archived_at: now,
        is_paused: true,
      })
      .eq("id", (ad as any).id)
      .eq("user_id", context.userId);
    if (error) throw new Error(`Kunde inte markera annonsen som såld: ${error.message}`);

    const { refundActivePromotionsForAd } = await import("./promotions.server");
    await refundActivePromotionsForAd(context.supabase, context.userId, (ad as any).id);
    return { ok: true, soldPrice: data.soldPrice ?? (ad as any).price };
  });

export const setAdPaused = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ slug: z.string().min(1), paused: z.boolean() }).parse(input),
  )
  .handler(async ({ data, context }) => {
    if (data.paused) await assertAuctionUnlocked(data.slug, context.userId, "pausa annonsen");

    // När en pausad annons startas igen granskar vi den på nytt, så att inget
    // konstigt smyger tillbaka ut på sajten.
    if (!data.paused) {
      const { data: existing } = await context.supabase
        .from("ads")
        .select("title, description, moderation_state")
        .eq("slug", data.slug)
        .eq("user_id", context.userId)
        .maybeSingle();
      if ((existing as any)?.moderation_state === "awaiting_price_approval") {
        throw new Error("Annonsen väntar på vårt godkännande av priset och kan inte startas än.");
      }
      if ((existing as any)?.moderation_state === "auto_paused") {
        throw new Error(
          "Annonsen är stoppad av oss och kan inte startas av dig. Kontakta supporten om du tror att det är fel.",
        );
      }
      if (existing) {
        const { checkQualityLocally } = await import("./quality");
        const junk = checkQualityLocally(
          (existing as any).title ?? "",
          (existing as any).description ?? "",
        );
        if (junk) {
          throw new Error(`${junk.reasons.join(" ")} Redigera annonsen innan du startar den igen.`);
        }
      }
    }

    // En annons som legat pausad förbi sitt utgångsdatum får ny livstid när
    // den startas igen – annars pausas den direkt av städjobbet.
    const renewPatch: Record<string, unknown> = {};
    if (!data.paused) {
      const { data: row } = await context.supabase
        .from("ads")
        .select("expires_at")
        .eq("slug", data.slug)
        .eq("user_id", context.userId)
        .maybeSingle();
      const expires = (row as any)?.expires_at ? new Date((row as any).expires_at).getTime() : 0;
      if (!expires || expires < Date.now()) {
        renewPatch["expires_at"] = new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString();
      }
    }

    const { data: updated, error } = await context.supabase
      .from("ads")
      .update({
        is_paused: data.paused,
        ...renewPatch,
        ...(data.paused ? {} : { moderation_state: "ok", moderation_reason: "" }),
      } as never)
      .eq("slug", data.slug)
      .eq("user_id", context.userId)
      .select("id, slug, is_paused")
      .maybeSingle();
    if (error) throw new Error(`Kunde inte uppdatera annonsen: ${error.message}`);
    if (!updated) throw new Error("Annonsen hittades inte eller tillhör inte dig.");

    let refunded = 0;
    if (data.paused) {
      const { refundActivePromotionsForAd } = await import("./promotions.server");
      refunded = await refundActivePromotionsForAd(
        context.supabase,
        context.userId,
        (updated as any).id,
      );
    }
    return { slug: updated.slug, is_paused: updated.is_paused, refundedPromotions: refunded };
  });

export const getMyAds = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("ads")
      .select(`id, ${AD_COLUMNS}`)

      .eq("user_id", context.userId)
      .is("deleted_at", null)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const ads = (data ?? []) as Record<string, unknown>[];

    // Auktioner med bud är låsta – listan behöver veta det för knapparna.
    const liveIds = ads
      .filter((a) => a.sale_kind === "auction" && a.auction_status === "live")
      .map((a) => a.id as string)
      .filter(Boolean);
    let bidCounts = new Map<string, number>();
    if (liveIds.length) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: bids } = await supabaseAdmin
        .from("bids")
        .select("ad_id")
        .in("ad_id", liveIds);
      bidCounts = (bids ?? []).reduce((map, row: any) => {
        map.set(row.ad_id, (map.get(row.ad_id) ?? 0) + 1);
        return map;
      }, new Map<string, number>());
    }

    return ads.map((a) => ({
      ...a,
      auctionLock: auctionLockState({
        saleKind: a.sale_kind as string | null,
        auctionStatus: a.auction_status as string | null,
        auctionEndsAt: a.auction_ends_at as string | null,
        bidCount: bidCounts.get(a.id as string) ?? 0,
        startPrice: a.start_price as number | null,
        reservePrice: a.reserve_price as number | null,
      }),
    })) as unknown as (typeof data extends null ? never : NonNullable<typeof data>[number] & {
      auctionLock: ReturnType<typeof auctionLockState>;
    })[];

  });

/** Summering av allt användaren har sålt – totalt, per kategori och per år. */
export const getMySalesSummary = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase
      .from("ads")
      .select("slug, title, category, sold_at, sold_price, price, image_url")
      .eq("user_id", context.userId)
      .not("sold_at", "is", null)
      .is("deleted_at", null)
      .order("sold_at", { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);

    const rows = (data ?? []).map((r: any) => ({
      slug: r.slug as string,
      title: r.title as string,
      category: r.category as string,
      soldAt: r.sold_at as string,
      amount: (r.sold_price ?? r.price ?? 0) as number,
      imageUrl: (r.image_url ?? "") as string,
    }));

    const byCategory = new Map<string, { count: number; amount: number }>();
    const byYear = new Map<string, { count: number; amount: number }>();
    for (const r of rows) {
      const year = r.soldAt.slice(0, 4);
      const c = byCategory.get(r.category) ?? { count: 0, amount: 0 };
      byCategory.set(r.category, { count: c.count + 1, amount: c.amount + r.amount });
      const y = byYear.get(year) ?? { count: 0, amount: 0 };
      byYear.set(year, { count: y.count + 1, amount: y.amount + r.amount });
    }

    return {
      total: { count: rows.length, amount: rows.reduce((s, r) => s + r.amount, 0) },
      byCategory: Array.from(byCategory, ([category, v]) => ({ category, ...v })).sort(
        (a, b) => b.amount - a.amount,
      ),
      byYear: Array.from(byYear, ([year, v]) => ({ year, ...v })).sort((a, b) =>
        b.year.localeCompare(a.year),
      ),
      sales: rows,
    };
  });

export const getAdBySlug = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => GetAdInput.parse(input))
  .handler(async ({ data }) => {
    const supabase = createSupabaseServerClient();
    let adQuery = supabase.from("ads").select(AD_COLUMNS).eq("slug", data.slug);
    adQuery = withoutTestAds(adQuery);
    const { data: found, error } = await adQuery.maybeSingle();
    let ad = found;

    // Dolda annonser filtreras bort av behörighetsreglerna – ägaren och
    // superadmin ska ändå kunna öppna dem.
    if (!ad) {
      const uid = await optionalUserId();
      if (uid) {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: hiddenAd } = await supabaseAdmin
          .from("ads")
          .select(AD_COLUMNS)
          .eq("slug", data.slug)
          .eq("hidden", true)
          .is("deleted_at", null)
          .maybeSingle();
        if (hiddenAd) {
          const { data: role } = await supabaseAdmin
            .from("user_roles")
            .select("role")
            .eq("user_id", uid)
            .eq("role", "superadmin")
            .maybeSingle();
          if (role || (hiddenAd as { user_id?: string }).user_id === uid) {
            ad = hiddenAd as typeof found;
          }
        }
      }
    }

    if (error && !ad) return null;
    if (!ad) return null;

    const requestedLanguage = await requestLanguage();
    const { data: localized } = await supabase
      .from("ad_translations")
      .select("title, description")
      .eq("ad_id", (ad as { id?: string }).id ?? "")
      .eq("language", requestedLanguage)
      .maybeSingle();
    if (localized) ad = { ...ad, title: localized.title, description: localized.description } as typeof ad;


    // Säljarens namn hämtas alltid från profilen – annonsens sparade
    // contact_name är en kopia från publiceringen och kan vara inaktuell.
    const a = ad as { user_id?: string | null; contact_name?: string | null };
    let sellerName = (a.contact_name ?? "") as string;
    if (a.user_id) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("display_name")
        .eq("user_id", a.user_id)
        .maybeSingle();
      const live = ((profile as { display_name?: string | null } | null)?.display_name ?? "").trim();
      if (live) sellerName = live;
    }

    // Säljer kontot som företag visas företagsprofilen och köpvillkoren i annonsen.
    let sellerBusiness: {
      name: string;
      orgNumber: string;
      slug: string;
      website: string | null;
      rightOfWithdrawal: string | null;
      warrantyPolicy: string | null;
      returnPolicy: string | null;
      vatRegistered: boolean;
    } | null = null;
    if (a.user_id) {
      const { data: biz } = await supabase
        .from("business_profiles")
        .select(
          "company_name, org_number, slug, website, right_of_withdrawal, warranty_policy, return_policy, vat_registered",
        )
        .eq("user_id", a.user_id)
        .maybeSingle();
      const { data: prof } = await supabase
        .from("profiles")
        .select("account_type")
        .eq("user_id", a.user_id)
        .maybeSingle();
      if (biz && prof?.account_type === "business") {
        sellerBusiness = {
          name: biz.company_name,
          orgNumber: biz.org_number,
          slug: biz.slug,
          website: biz.website,
          rightOfWithdrawal: biz.right_of_withdrawal,
          warrantyPolicy: biz.warranty_policy,
          returnPolicy: biz.return_policy,
          vatRegistered: biz.vat_registered !== false,
        };
      }
    }

    return {
      ...(ad as Record<string, unknown>),
      contact_name: sellerName,
      seller_business: sellerBusiness,
    } as typeof ad & { seller_business: typeof sellerBusiness };
  });


function applyAdFilters<T extends { eq: any; in: any; or: any }>(
  query: T,
  data: {
    category?: string;
    categories?: string[];
    search?: string;
    saleKind?: string;
    country?: string;
  },
): T {
  let q: any = withoutTestAds(query as any);
  // Marknad: annonser visas bara i sitt eget säljland.
  q = q.eq("country", data.country ?? "SE");
  const cats = data.categories?.length ? data.categories : data.category ? [data.category] : [];
  if (cats.length === 1) q = q.eq("category", cats[0]);
  else if (cats.length > 1) q = q.in("category", cats);
  // Fast pris eller auktion – annonser utan värde räknas som fast pris.
  if (data.saleKind === "auction") q = q.eq("sale_kind", "auction");
  else if (data.saleKind === "fixed") q = q.neq("sale_kind", "auction");
  if (data.search) {
    const search = data.search
      .trim()
      .replace(/[%_(),]/g, " ")
      .replace(/\s+/g, " ")
      .slice(0, 80);
    if (search) {
      q = q.or(`title.ilike.%${search}%,description.ilike.%${search}%,location.ilike.%${search}%`);
    }
  }
  return q as T;
}

export const listAds = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => ListAdsInput.parse(input ?? {}))
  .handler(async ({ data }) => {
    const supabase = createSupabaseServerClient();
    const nowIso = new Date().toISOString();
    const limit = data.limit ?? 40;
    const offset = data.offset ?? 0;
    let query = supabase
      .from("ads")
      .select(
        "slug, user_id, image_url, image_urls, image_focus, video_url, title, description, price, category, location, created_at, latitude, longitude, promoted_until, sale_kind, auction_ends_at, auction_status",
      )
      .eq("is_paused", false)
      .is("archived_at", null)
      .is("deleted_at", null)
      // Sidindelning – klienten kan hämta sida efter sida utan tak på 1 000 annonser.
      .range(offset, offset + limit - 1);

    const endingSoon = data.sort === "slutar-snart";
    if (endingSoon) {
      // Bara auktioner som fortfarande pågår, den som slutar först överst.
      query = query
        .eq("sale_kind", "auction")
        .eq("auction_status", "live")
        .gt("auction_ends_at", nowIso)
        .order("auction_ends_at", { ascending: true });
    } else if (data.sort === "billigast") query = query.order("price", { ascending: true });
    else if (data.sort === "dyrast") query = query.order("price", { ascending: false });
    else query = query.order("created_at", { ascending: false });
    // Stabil sekundär sortering så att sidorna inte överlappar varandra.
    query = query.order("slug", { ascending: true });

    query = applyAdFilters(query, data);

    const bizIds = await businessUserIds(supabase);
    const filtered = applySellerTypeFilter(query, data.sellerType, bizIds);
    if (!filtered) return [];

    const { data: ads, error } = await filtered;
    if (error) throw new Error(error.message);

    const bizSet = new Set(bizIds);
    // Märkningen "Företag" följer med varje annons så att listan kan visa den direkt.
    // Beskrivningen kapas: korten visar bara två rader, men hela texten hade
    // annars följt med både i HTML-svaret och i klientens datacache.
    const localizedAds = await localizeAds(supabase, (ads ?? []) as any[]);
    const rows = localizedAds.map((a: any) => ({
      ...a,
      description: smartTruncate(String(a.description ?? ""), 160),
      seller_business: !!a.user_id && bizSet.has(a.user_id),
    }));

    if (endingSoon) return rows;
    const isPromoted = (a: { promoted_until?: string | null }) =>
      !!a.promoted_until && a.promoted_until > nowIso;
    return [...rows].sort((a, b) => Number(isPromoted(b)) - Number(isPromoted(a)));
  });

export const countAds = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => ListAdsMapInput.parse(input ?? {}))
  .handler(async ({ data }) => {
    const supabase = createSupabaseServerClient();
    let query = supabase
      .from("ads")
      .select("slug", { count: "exact", head: true })
      .eq("is_paused", false)
      .is("archived_at", null)
      .is("deleted_at", null);
    query = applyAdFilters(query, data);
    const filtered = applySellerTypeFilter(query, data.sellerType, await businessUserIds(supabase));
    if (!filtered) return 0;
    const { count, error } = await filtered;
    if (error) throw new Error(error.message);
    return count ?? 0;
  });

export const listAdsForMap = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => ListAdsMapInput.parse(input ?? {}))
  .handler(async ({ data }) => {
    const supabase = createSupabaseServerClient();
    let query = supabase
      .from("ads")
      .select("slug, image_url, title, price, category, location, latitude, longitude")
      .eq("is_paused", false)
      .is("archived_at", null)
      .is("deleted_at", null)
      .not("latitude", "is", null)
      .not("longitude", "is", null)
      .order("created_at", { ascending: false })
      .limit(2000);
    query = withoutTestAds(query).eq("country", data.country ?? "SE");

    const mapCats = data.categories?.length
      ? data.categories
      : data.category
        ? [data.category]
        : [];
    if (mapCats.length === 1) query = query.eq("category", mapCats[0]);
    else if (mapCats.length > 1) query = query.in("category", mapCats);
    if (data.search) {
      const search = data.search
        .trim()
        .replace(/[%_(),]/g, " ")
        .replace(/\s+/g, " ")
        .slice(0, 80);
      if (search) {
        query = query.or(`title.ilike.%${search}%,location.ilike.%${search}%`);
      }
    }

    const { data: ads, error } = await query;
    if (error) throw new Error(error.message);
    return ads ?? [];
  });

export const getAdHistory = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => GetAdInput.parse(input))
  .handler(async ({ data }) => {
    const supabase = createSupabaseServerClient();
    const { data: ad } = await supabase
      .from("ads")
      .select("id")
      .eq("slug", data.slug)
      .maybeSingle();
    if (!ad) return [];

    // Historiken är låst i databasen (endast ägare/admin). Den publika pris- och
    // ändringstidslinjen läses därför via servern, som bara returnerar ofarliga fält.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: history, error } = await supabaseAdmin
      .from("ad_history")
      .select("id, field, old_value, new_value, created_at")
      .eq("ad_id", ad.id)
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) throw new Error(error.message);
    return history ?? [];
  });

/** Endast för annonsägaren: innehåller kontaktuppgifter som aldrig visas publikt. */
export const getMyAdForEdit = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => GetAdInput.parse(input))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: ad, error } = await supabaseAdmin
      .from("ads")
      .select(`${AD_COLUMNS}, contact_email, contact_phone`)
      .eq("slug", data.slug)
      .eq("user_id", context.userId)
      .maybeSingle();

    if (error) throw new Error(error.message);
    if (!ad) throw new Error("Annonsen hittades inte eller tillhör inte dig.");
    const { lock } = await loadAuctionLock(data.slug, context.userId);
    return { ...(ad as Record<string, unknown>), auctionLock: lock } as typeof ad & {
      auctionLock: typeof lock;
    };

  });

/**
 * Kontaktuppgifter för den inloggade användaren: visningsnamn och telefon
 * hämtas från profilen, e-post från kontot. Faller tillbaka på senaste
 * annonsen för den som ännu inte fyllt i sin profil.
 */
export const getMyContactDefaults = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const [{ data: profile }, { data: contact }] = await Promise.all([
      context.supabase
        .from("profiles")
        .select("display_name")
        .eq("user_id", context.userId)
        .maybeSingle(),
      context.supabase
        .from("profile_contacts")
        .select("phone")
        .eq("user_id", context.userId)
        .maybeSingle(),
    ]);

    const email = (context.claims?.email as string | undefined) ?? "";
    const name = (profile?.display_name ?? "").trim();
    const phone = (contact?.phone ?? "").trim();

    if (name) {
      return { contactName: name, contactEmail: email, contactPhone: phone, fromProfile: true };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("ads")
      .select("contact_name, contact_email, contact_phone")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    return {
      contactName: name || (data?.contact_name ?? ""),
      contactEmail: email || (data?.contact_email ?? ""),
      contactPhone: phone || (data?.contact_phone ?? ""),
      fromProfile: !!name,
    };
  });


export const listRelatedAds = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z
      .object({
        slug: z.string().min(1),
        category: z.string().min(1),
        latitude: z.number().nullish(),
        longitude: z.number().nullish(),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    const supabase = createSupabaseServerClient();
    const cols =
      "slug, image_url, image_urls, image_focus, video_url, title, price, category, location, created_at, latitude, longitude";

    // Interna AI-taggar för den aktuella annonsen – används bara för matchning, returneras aldrig.
    const { data: current } = await supabase
      .from("ads")
      .select("tags")
      .eq("slug", data.slug)
      .maybeSingle();
    const tags = ((current?.tags as string[] | null) ?? []).slice(0, 8);

    let tagMatches: Array<Record<string, unknown>> = [];
    if (tags.length) {
      const res = await withoutTestAds(
        supabase
        .from("ads")
        .select(`${cols}, tags`)
        .eq("is_paused", false)
        .is("archived_at", null)
        .is("deleted_at", null)
        .neq("slug", data.slug)
        .overlaps("tags", tags)
        .order("created_at", { ascending: false })
        .limit(30),
      );
      const tagSet = new Set(tags);
      tagMatches = (res.data ?? [])
        .map((a: any) => ({
          ...a,
          _score: ((a.tags as string[] | null) ?? []).filter((t) => tagSet.has(t)).length,
        }))
        .sort((a: any, b: any) => b._score - a._score)
        .map(({ tags: _t, _score, ...rest }: any) => rest);
    }

    const similarRes = await withoutTestAds(
      supabase
      .from("ads")
      .select(cols)
      .eq("is_paused", false)
      .is("archived_at", null)
      .is("deleted_at", null)
      .eq("category", data.category)
      .neq("slug", data.slug)
      .order("created_at", { ascending: false })
      .limit(6),
    );

    // "I närheten" = max 30 km fågelvägen, mätt med Haversine mot annonsens koordinater.
    const NEARBY_MAX_KM = 30;
    type NearbyAd = NonNullable<typeof similarRes.data>[number] & { distanceKm: number };
    let nearby: NearbyAd[] = [];
    if (data.latitude != null && data.longitude != null) {
      const dLat = NEARBY_MAX_KM / 111; // ~0,27 grader
      const dLon = NEARBY_MAX_KM / (111 * Math.max(Math.cos((data.latitude * Math.PI) / 180), 0.2));
      const res = await withoutTestAds(
        supabase
        .from("ads")
        .select(cols)
        .eq("is_paused", false)
        .is("archived_at", null)
        .is("deleted_at", null)
        .neq("slug", data.slug)
        .not("latitude", "is", null)
        .not("longitude", "is", null)
        .gte("latitude", data.latitude - dLat)
        .lte("latitude", data.latitude + dLat)
        .gte("longitude", data.longitude - dLon)
        .lte("longitude", data.longitude + dLon)
        .order("created_at", { ascending: false })
        .limit(60),
      );
      const origin = { latitude: data.latitude, longitude: data.longitude };
      nearby = (res.data ?? [])
        .map((a: any) => ({
          ...a,
          distanceKm:
            a.latitude == null || a.longitude == null
              ? null
              : distanceKm(origin, { latitude: a.latitude, longitude: a.longitude }),
        }))
        .filter((a: any) => a.distanceKm != null && a.distanceKm <= NEARBY_MAX_KM)
        .sort((a: any, b: any) => a.distanceKm - b.distanceKm) as NearbyAd[];
    }

    // Taggmatchningar först, därefter samma kategori.
    const byTagSlugs = new Set(tagMatches.map((a: any) => a.slug));
    const categoryMatches = (similarRes.data ?? []).filter((a) => !byTagSlugs.has(a.slug));
    const similar = [...tagMatches, ...categoryMatches].slice(0, 4) as typeof categoryMatches;

    const similarSlugs = new Set(similar.map((a: any) => a.slug));
    return {
      similar,
      nearby: nearby.filter((a) => !similarSlugs.has(a.slug)).slice(0, 4),
    };
  });

/** Skapar en signerad uppladdnings-URL för video (endast Pro). */
export const createVideoUpload = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        mimeType: z.string().min(1).max(80),
        sizeBytes: z.number().int().positive(),
        // Kvittot från ljudgranskningen. Utan det får filmen aldrig kopplas
        // till en annons – kontrollkedjan slår ifrån vid publicering.
        audioClearanceId: z.string().uuid(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { plan, unlimited } = await readPlanAndUsage(context.supabase as never, context.userId);
    if (!limitsFor(plan, unlimited).video) {
      throw new Error("Video ingår i Pro. Uppgradera för att lägga till video.");
    }
    if (!/^video\//i.test(data.mimeType)) throw new Error("Endast videofiler tillåts.");
    if (data.sizeBytes > 100 * 1024 * 1024) throw new Error("Videon får vara max 100 MB.");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Klartecknet måste vara vårt eget, godkänt och inte redan använt.
    const { data: clearance } = await supabaseAdmin
      .from("video_audio_clearances")
      .select("id, ok, video_path")
      .eq("id", data.audioClearanceId)
      .eq("user_id", context.userId)
      .maybeSingle();
    const row = clearance as { id: string; ok: boolean; video_path: string | null } | null;
    if (!row) throw new Error("Ljudgranskningen saknas. Prova att lägga till videon igen.");
    if (!row.ok) throw new Error("Ljudet i videon innehåller kontaktuppgifter.");
    if (row.video_path) throw new Error("Ljudgranskningen är redan använd.");

    const ext = (data.mimeType.split("/")[1] || "mp4").replace(/[^a-z0-9]/gi, "");
    const path = `videos/${context.userId}/${Date.now()}-${generateSlug()}.${ext}`;
    const { data: signed, error } = await supabaseAdmin.storage
      .from("ad-images")
      .createSignedUploadUrl(path);
    if (error || !signed) throw new Error("Kunde inte förbereda videouppladdningen.");

    await supabaseAdmin
      .from("video_audio_clearances")
      .update({ video_path: path, used_at: new Date().toISOString() } as never)
      .eq("id", row.id);

    return { path, token: signed.token, signedUrl: signed.signedUrl };
  });

